/**
 * Checks if a value can be expanded (has nested content)
 * @param {Object} value - The value object from the values array
 * @returns {boolean} - True if the value can be expanded
 */
export function isExpandableValue(value) {
  if (!value || !value.value_content) return false;

  const { value_type, value_content } = value;

  if (value_type === 'object') {
    return (
      value_content.display_value &&
      Array.isArray(value_content.display_value) &&
      value_content.display_value.length > 0
    );
  }

  if (value_type === 'array') {
    return Array.isArray(value_content) && value_content.length > 0;
  }

  return false;
}

/**
 * Formats a value for display
 * @param {Object} value - The value object from the values array
 * @param {string} fallbackClassName - Optional fallback class name
 * @returns {string} - Formatted display string
 */
export function formatValueDisplay(value, fallbackClassName = null) {
  if (!value) return 'null';

  const className = fallbackClassName || value.class_name || 'Unknown';
  const displayValue = value.value_content && value.value_content.display_value;
  const hashCode = (value.value_content && value.value_content.identity_hash_code) || '';

  const hexadecimalHashCode = hashCode ? parseInt(hashCode, 10).toString(16) : '';

  const hashSuffix = hexadecimalHashCode ? '@' + hexadecimalHashCode : '';

  switch (value.value_type) {
    case 'primitive':
      return `(${className}) ${displayValue || 'null'}`;
    case 'string':
      return `(${className}) "${displayValue || ''}"`;
    case 'hidden':
      return `(${className}) HIDDEN${hashSuffix}`;
    case 'array':
      return `(${className}${hashSuffix})`;
    case 'object':
      return `(${className}${hashSuffix})`;
    default:
      return `(${className}) ${displayValue || 'null'}`;
  }
}

/**
 * Gets nested fields from a value (works for both objects and arrays)
 * @param {Object} value - The value object from the values array
 * @returns {Array} - Array of field objects
 */
export function getNestedFields(value) {
  if (!value || !isExpandableValue(value)) return [];

  const { value_type, value_content } = value;

  if (value_type === 'object') {
    return value_content.display_value;
  }

  if (value_type === 'array') {
    return value_content.map((item) => ({
      name: `[${item.index}]`,
      value_id: item.value_id,
      is_field: false,
      class_name: null,
      type: 'array_element',
      index: item.index,
    }));
  }

  return [];
}

/**
 * Checks if at least one nested field/element resolves to an existing value
 */
export function hasResolvableNestedValues(value, values) {
  if (!value || !isExpandableValue(value)) return false;

  const fields = getNestedFields(value);
  return fields.some((field) => field.value_id && values.some((v) => v.id === field.value_id));
}
